import { PROBLEM_CONTENT_TYPE, type ProblemFieldError, ProblemDetailsSchema } from '@gestion-permisos/contracts';

/**
 * Error de la API en formato Problem Details (09 §3). La interfaz muestra `title`, `detail` y `traceId`;
 * nunca reimplementa la regla que lo provocó.
 */
export class ApiProblem extends Error {
  constructor(
    readonly status: number,
    readonly title: string,
    readonly code: string | undefined,
    readonly detail: string | undefined,
    readonly traceId: string | undefined,
    readonly errors: readonly ProblemFieldError[],
  ) {
    super(title);
    this.name = 'ApiProblem';
  }

  /**
   * Lee el cuerpo de una respuesta de error. Si cumple el contrato se usa tal cual; si no (por ejemplo, un
   * `code` que esta versión de la web no conoce), se leen los campos básicos sin romper la pantalla.
   */
  static async from(response: Response): Promise<ApiProblem> {
    const isProblem = (response.headers.get('content-type') ?? '').startsWith(PROBLEM_CONTENT_TYPE);
    const body: unknown = isProblem ? await response.json().catch(() => undefined) : undefined;
    const parsed = ProblemDetailsSchema.safeParse(body);
    if (parsed.success) {
      const problem = parsed.data;
      return new ApiProblem(response.status, problem.title, problem.code, problem.detail, problem.traceId, problem.errors ?? []);
    }
    const loose = body !== null && typeof body === 'object' ? (body as Record<string, unknown>) : {};
    const text = (key: string) => (typeof loose[key] === 'string' ? loose[key] : undefined);
    return new ApiProblem(response.status, text('title') ?? 'Error inesperado', text('code'), text('detail'), text('traceId'), []);
  }
}
